import { requestWebNotifications, webInboxConfigured } from './cleverTapWeb'

export type InboxMessage = {
  id: string
  leadId: string
  recipientEmployeeId: string
  title: string
  body: string
  createdAt: number
  expiresAt: number
  isRead: boolean
}

export type InboxSnapshot = {
  configured: boolean
  ready: boolean
  employeeId: string | null
  messages: InboxMessage[]
  totalCount: number
  unreadCount: number
}

export type LeadDestination = { leadId: string; recipientEmployeeId: string }
type NativeBridge = {
  postMessage: (json: string) => void
  onmessage?: (event: MessageEvent<string>) => void
}
declare global { interface Window { CleverTapNative?: NativeBridge } }

let requestSequence = 0
const pending = new Map<string, { resolve: (data: any) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>()

export const connectNotificationBridge = (onEvent: (event: string, data: any) => void) => {
  const bridge = window.CleverTapNative
  if (!bridge) return () => undefined
  bridge.onmessage = ({ data }) => {
    let response: any
    try { response = JSON.parse(data) } catch { return }
    const request = pending.get(response?.requestId)
    if (request) {
      clearTimeout(request.timer)
      pending.delete(response.requestId)
      if (response.error) request.reject(new Error(response.error))
      else request.resolve(response.result)
    } else if (typeof response?.event === 'string') onEvent(response.event, response.data)
  }
  return () => {
    bridge.onmessage = undefined
    pending.forEach(({ timer, reject }) => {
      clearTimeout(timer)
      reject(new Error('Notification connection closed'))
    })
    pending.clear()
  }
}

export const requestNotifications = <T,>(operation: string, data: object = {}): Promise<T> => {
  const bridge = window.CleverTapNative
  if (!bridge) return requestWebNotifications<T>(operation, data)
  return new Promise((resolve, reject) => {
    const requestId = String(++requestSequence)
    const timer = setTimeout(() => {
      pending.delete(requestId)
      reject(new Error('Notification request timed out. Please retry.'))
    }, 10000)
    pending.set(requestId, { resolve, reject, timer })
    try { bridge.postMessage(JSON.stringify({ requestId, operation, ...data })) }
    catch (error) { clearTimeout(timer); pending.delete(requestId); reject(error) }
  })
}

export const notificationsConfigured = () => !!window.CleverTapNative || webInboxConfigured()

export const normalizeInbox = (data: any, employeeId: string | null, now = Date.now()): InboxSnapshot => {
  const messages: InboxMessage[] = employeeId && data?.employeeId === employeeId && Array.isArray(data.messages)
    ? data.messages.filter((message: InboxMessage) => message && typeof message.id === 'string' &&
      message.recipientEmployeeId === employeeId && Number.isFinite(message.createdAt) &&
      Number.isFinite(message.expiresAt) && (message.expiresAt <= 0 || message.expiresAt > now))
      .sort((a: InboxMessage, b: InboxMessage) => b.createdAt - a.createdAt)
    : []
  return { configured: data?.configured === true,
    ready: data?.ready === true && data?.employeeId === employeeId && employeeId !== null,
    employeeId, messages, totalCount: messages.length,
    unreadCount: messages.filter((message) => !message.isRead).length }
}

export const getLeadNotificationPath = (destination: LeadDestination): string => {
  if (typeof destination?.leadId !== 'string' || !destination.leadId.trim() ||
      typeof destination.recipientEmployeeId !== 'string' || !destination.recipientEmployeeId.trim()) {
    throw new Error('Notification has no valid lead destination')
  }
  return `/notification/lead/${encodeURIComponent(destination.leadId)}?recipientEmployeeId=${encodeURIComponent(destination.recipientEmployeeId)}`
}
